// file-stc file details (GET /api/files?ids=…). A READY file never changes, so
// details are cached on the device for good: an in-memory map plus IndexedDB,
// both keyed by file id. PENDING files (only their owner sees them) are not cached.
import client from "./client";
import logger from "../lib/logger";

export type FileKind = "IMAGE" | "VIDEO" | "AUDIO" | "DOCUMENT";

export interface FileInfo {
  id: string;
  kind: FileKind;
  mime: string;
  ext: string;
  size: number;
  width: number | null;
  height: number | null;
  durationMs: number | null;
  /** A READY PDF: its page count. */
  pageCount?: number | null;
  url: string;
  thumbnailFileId: string | null;
  /** Present for the owner only. */
  ownerUserId?: string;
  /** Present for the owner only. */
  status?: "PENDING" | "READY";
}

/** file-stc caps one GET /api/files?ids= at 100 ids. */
export const MAX_IDS_PER_GET = 100;

const DB_NAME = "whoisit-files";
const STORE = "files";

const memory = new Map<string, FileInfo>();
const inflight = new Map<string, Promise<FileInfo | undefined>>();
let dbPromise: Promise<IDBDatabase | null> | null = null;

function cacheable(info: FileInfo): boolean {
  return info.status === undefined || info.status === "READY";
}

function openDb(): Promise<IDBDatabase | null> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise<IDBDatabase | null>((resolve) => {
    try {
      if (typeof indexedDB === "undefined") return resolve(null);
      const req = indexedDB.open(DB_NAME, 1);
      req.onupgradeneeded = () => {
        req.result.createObjectStore(STORE, { keyPath: "id" });
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(null);
      req.onblocked = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
  return dbPromise;
}

async function idbGet(ids: string[]): Promise<FileInfo[]> {
  const db = await openDb();
  if (!db || ids.length === 0) return [];
  return new Promise((resolve) => {
    try {
      const store = db.transaction(STORE, "readonly").objectStore(STORE);
      const out: FileInfo[] = [];
      let left = ids.length;
      for (const id of ids) {
        const req = store.get(id);
        req.onsuccess = () => {
          if (req.result) out.push(req.result as FileInfo);
          if (--left === 0) resolve(out);
        };
        req.onerror = () => {
          if (--left === 0) resolve(out);
        };
      }
    } catch {
      resolve([]);
    }
  });
}

async function idbPut(infos: FileInfo[]): Promise<void> {
  const db = await openDb();
  if (!db || infos.length === 0) return;
  try {
    const tx = db.transaction(STORE, "readwrite");
    // A failed write (quota, closed by a logout) only costs a later re-fetch.
    tx.onerror = (ev) => {
      ev.preventDefault?.();
      logger.warn("[files] IndexedDB write failed", tx.error);
    };
    tx.onabort = () => logger.warn("[files] IndexedDB write aborted", tx.error);
    const store = tx.objectStore(STORE);
    for (const info of infos) store.put(info);
  } catch (err) {
    logger.warn("[files] IndexedDB write failed", err);
  }
}

/** Puts READY details in both caches (e.g. the confirm response). */
export function rememberFile(info: FileInfo): void {
  if (!cacheable(info)) return;
  memory.set(info.id, info);
  void idbPut([info]);
}

/** Synchronous memory-cache read, for a first render without a flash. */
export function cachedFile(id: string): FileInfo | undefined {
  return memory.get(id);
}

// Ids asked for in the same tick share one batched request (every bubble of a
// screen calls getFiles on its own).
let queued = new Set<string>();
let queuedBatch: Promise<Record<string, FileInfo>> | null = null;

function enqueue(ids: string[]): Promise<Record<string, FileInfo>> {
  ids.forEach((id) => queued.add(id));
  if (!queuedBatch) {
    queuedBatch = new Promise<void>((r) => setTimeout(r, 0)).then(() => {
      const batch = [...queued];
      queued = new Set();
      queuedBatch = null;
      return fetchMissing(batch);
    });
  }
  return queuedBatch;
}

async function fetchMissing(ids: string[]): Promise<Record<string, FileInfo>> {
  const found: Record<string, FileInfo> = {};
  for (let i = 0; i < ids.length; i += MAX_IDS_PER_GET) {
    const chunk = ids.slice(i, i + MAX_IDS_PER_GET);
    const { data } = await client.get<{
      result: { files: Record<string, FileInfo> };
    }>("/api/files", { params: { ids: chunk.join(",") } });
    Object.assign(found, data.result?.files ?? {});
  }
  const keep = Object.values(found).filter(cacheable);
  keep.forEach((f) => memory.set(f.id, f));
  void idbPut(keep);
  return found;
}

/**
 * Details for each id the caller may see (unknown ids are left out).
 * Each id is fetched at most once per device while it is READY.
 */
export async function getFiles(
  ids: string[],
): Promise<Record<string, FileInfo>> {
  const out: Record<string, FileInfo> = {};
  const wanted = [...new Set(ids.filter(Boolean))];
  let missing = wanted.filter((id) => {
    const hit = memory.get(id);
    if (hit) out[id] = hit;
    return !hit;
  });
  if (missing.length === 0) return out;

  for (const info of await idbGet(missing)) {
    memory.set(info.id, info);
    out[info.id] = info;
  }
  missing = missing.filter((id) => !out[id]);

  const waits: Promise<void>[] = [];
  const toFetch: string[] = [];
  for (const id of missing) {
    const pending = inflight.get(id);
    if (pending)
      waits.push(
        pending.then((f) => {
          if (f) out[id] = f;
        }),
      );
    else toFetch.push(id);
  }
  if (toFetch.length > 0) {
    const batch = enqueue(toFetch);
    for (const id of toFetch) {
      const one = batch.then((r) => r[id]);
      inflight.set(id, one);
      waits.push(
        one.then((f) => {
          if (f) out[id] = f;
        }),
      );
    }
    batch
      .catch(() => undefined)
      .finally(() => toFetch.forEach((id) => inflight.delete(id)));
  }
  await Promise.all(waits);
  return out;
}

/**
 * Sign-out: forget every file detail of this account (memory and the IndexedDB
 * database), so the next account starts empty. Never throws.
 */
export async function clearFileCache(): Promise<void> {
  memory.clear();
  inflight.clear();
  queued = new Set();
  const pending = dbPromise;
  dbPromise = null;
  try {
    const db = pending ? await pending : null;
    db?.close();
    if (typeof indexedDB === "undefined") return;
    await new Promise<void>((resolve) => {
      const req = indexedDB.deleteDatabase(DB_NAME);
      req.onsuccess = () => resolve();
      req.onerror = () => resolve();
      req.onblocked = () => resolve();
    });
  } catch (err) {
    logger.warn("[files] clearing the device cache failed", err);
  }
}

/** Test hook: forget the memory cache and the IndexedDB handle. */
export function resetFileCacheForTests(): void {
  memory.clear();
  inflight.clear();
  queued = new Set();
  queuedBatch = null;
  dbPromise = null;
}
